import { PlayListNodeUploadDto, PlaylistContentDownloadDto } from "collaboration-service";
import { AutoGrid, AutoGridColumn, Button, Icon, LoaderInline, Theme, useThemePart } from "imaginarity-react-ui";
import * as _ from "lodash";
import * as React from "react";
import { useSelector } from 'react-redux';
import { ApplicationState } from "services/ApplicationState/ApplicationState";
import { countElementUsageInNodes, generateUUID, shallowCompare, visit } from "services/Helpers";
import ScrollHelper from 'services/Helpers/ScrollHelper';
import { useImgI18N } from "services/ImgI18N";
import { PlaylistEditState, useSelectorPLE } from "./PlaylistEdit";
import { AGCTitle, BtnCon, ImplementedElementsHeadlineOnlyAAO, SequenceContainerOuter } from "./PlaylistEditClasses";
import { getPlaylistContentData } from "./PlaylistEditContentImplementedElement";
import PlaylistEditLinearNode, { getCurrentPlaylistNode } from "./PlaylistEditLinearNode";
import PlaylistEditSequenceImplementedElement from "./PlaylistEditSequenceImplementedElement";
import { PlaylistEditSequenceLinearSC as T } from "./PlaylistEditSequenceLinearSC";
import { usePLEState } from "./usePLEState";


// const valuesPlaylistEdgeTransitionFunctionReduced = _.filter(valuesPlaylistEdgeTransitionFunction, value =>
//     value === "FallThru" || value === "PercentageDone"
// );

const mapper = (state: ApplicationState) => ({
    missing: state.playlistsState.missing,
    contentLanguage: state.contentLanguage,
});

interface SchemeColor {
    value: string;
    label: string;
}

export const playlistEditNodeSchemeColors: SchemeColor[] = [
    { value: "@darkGrey", label: "Dark Grey" },
    { value: "@accentRed", label: "Red" },
    { value: "@accentOrange", label: "Orange" },
    { value: "@accentYellow", label: "Yellow" },
    { value: "@accentGreen", label: "Green" },
    { value: "@accentTurquoise", label: "Turquoise" },
    { value: "@accentBlue", label: "Blue" },
    { value: "@accentLightBlue", label: "Light Blue" },
    { value: "@accentPurple", label: "Purple" },
    { value: "@accentPink", label: "Pink" },
];

interface PlaylistEditSequenceLinearProps {
}

const pleMapper = (state: PlaylistEditState) => ({
    posts: state.posts,
    playlist: state.playlist,
    expertMode: state.expertMode,
    contents: state.playlist?.contents ?? [],
});

const PlaylistEditSequenceLinear = (p: PlaylistEditSequenceLinearProps) => {
    const { posts, contents: playlistContents } = useSelectorPLE(pleMapper, shallowCompare);
    const s_startNode = usePLEState(s => s.startNode);
    const { contentLanguage } = useSelector(mapper, shallowCompare);
    const { t, currentLanguage } = useImgI18N("playlist");
    const [scrollHelper] = React.useState(new ScrollHelper());
    // const [expertMode, setExpertMode] = React.useState<boolean>(false);
    const [showExpandedView, setShowExpandedView] = React.useState<boolean>(true);
    const [rightWidth, setRightWidth] = React.useState<number>(0);
    const [curNode, setCurNode] = React.useState<string>();

    const cHeight = useThemePart((t: Theme) => t.content.height);
    const maxLeftHeight = cHeight - 159;
    const maxRightHeight = cHeight - 200;
    let selectedNodeColor: string | undefined = undefined;

    const lng = contentLanguage ?? currentLanguage

    const leftContainerRef = React.useRef<HTMLDivElement | null>(null);
    const rightContainerRef = React.useRef<HTMLDivElement | null>(null);

    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const [containerWidth, setContainerWidth] = React.useState<number>(500);

    const setContainerRef = React.useCallback((node: HTMLDivElement | null) => {
        if (node !== null) {
            containerRef.current = node;
            setContainerWidth(node.offsetWidth);
        }
    }, []);


    const contents = React.useMemo(() => {
        const cts = _.map(playlistContents, pc => {
            const post = _.find(posts, s => s.id === pc.referenceId)!;
            if (post)
                return { contents: pc, post, title: getPlaylistContentData(pc, lng, post).title };
            return undefined;
        });
        return _.compact(cts);
    }, [playlistContents, posts, lng]);

    React.useEffect(() => {
        if (!containerRef.current) return;
        const updateWidth = () => {
            if (containerRef.current) {
                setContainerWidth(containerRef.current.offsetWidth);
            }
        };
        const observer = new ResizeObserver(updateWidth);
        observer.observe(containerRef.current);
        window.addEventListener("resize", updateWidth);
        return () => {
            observer.disconnect();
            window.removeEventListener("resize", updateWidth);
        };
    }, [rightWidth]);

    const avlWidth = containerWidth - 12;

    const firstNodeId = s_startNode.value?.id;
    React.useEffect(() => {
        setCurNode(firstNodeId);
    }, [firstNodeId, setCurNode]);

    const updateWidths = () => {
        if (leftContainerRef.current && rightContainerRef.current) {
            const rightWidthValue = rightContainerRef.current.offsetWidth - parseInt(window.getComputedStyle(rightContainerRef.current).paddingLeft, 10) - parseInt(window.getComputedStyle(rightContainerRef.current).paddingRight, 10) - parseInt(window.getComputedStyle(rightContainerRef.current).borderLeftWidth, 10) - parseInt(window.getComputedStyle(rightContainerRef.current).borderRightWidth, 10);

            setRightWidth(rightWidthValue);
        }
    };

    React.useEffect(() => {
        updateWidths();
        window.addEventListener('resize', updateWidths);
        return () => {
            window.removeEventListener('resize', updateWidths);
        };

    }, []);

    const nodes = React.useMemo(() => {
        const nodes: PlayListNodeUploadDto[] = [];
        visit(s_startNode.value, (cur) => _.compact(_.map(cur.children, edge => edge.to)), cur => nodes.push(cur));
        return nodes;
    }, [s_startNode.value]);

    if (nodes) {
        const selectedNode = nodes.find(node => node.id === curNode);
        if (selectedNode && selectedNode.color) {
            selectedNodeColor = selectedNode.color;
        }
    }

    const addNode = React.useMemo(() => () => {
        const startNode = s_startNode.clonedValue();
        const id = generateUUID();
        let lastNode = startNode;
        while (lastNode && lastNode.children?.[0]?.to)
            lastNode = lastNode.children[0].to;
        const newNode: PlayListNodeUploadDto = {
            headlines: [],
            id,
            referenceIds: [],
            descriptions: [],
            // absDueDate: undefined,
            absDueDate: lastNode?.absDueDate ?? undefined,
            color: playlistEditNodeSchemeColors[0].value,
            dueDateRelativeInHours: lastNode?.dueDateRelativeInHours ?? 168,
            // dueDateRelativeTo: "PlaylistAssignment",
            dueDateRelativeTo: lastNode?.dueDateRelativeTo ?? "PlaylistAssignment",
            durationInMinutes: 10,
            children: [
                {
                    transitionFunction: "PercentageDone",
                    referenceValue: 1
                }
            ],
        };
        if (!startNode) {
            s_startNode.setValue(newNode);
        } else {
            if (lastNode) {
                if (!lastNode.children || !lastNode.children[0])
                    lastNode.children = [{
                        transitionFunction: "AbsCountDone",
                        referenceValue: 1.0,
                        to: newNode
                    }];
                else
                    lastNode.children[0].to = newNode;
            }
            s_startNode.setValue(startNode);
        }
        setCurNode(newNode.id);
        scrollHelper.scrollTo(id)
    }, [setCurNode, s_startNode, scrollHelper]);

    React.useEffect(() => {
        const nodesLength = nodes.length;
        if (nodesLength === 0) {
            addNode();
        }
    }, [addNode, nodes.length]);

    const sortedContents = _.sortBy(contents, c => c.contents.referenceType);
    const groupedContents = _.groupBy(sortedContents, c => c.contents.referenceType);
    const minNodeWith = 650;
    const isLarge = rightWidth > minNodeWith;
    const showExpView = (isLarge) && showExpandedView === true;

    const getPostHeadlineForContent = React.useMemo(() => (content: PlaylistContentDownloadDto): string => {
        // console.log("PlaylistEditSequenceLinear.tsx::461 => posts", posts);

        const pc = _.find(posts, (c) => c.id === content.referenceId);
        // console.log("PlaylistEditSequenceLinear.tsx::464 => pc", pc);

        return getPlaylistContentData(content, lng, pc)?.title ?? "";
    }, [posts, lng]);

    const selectAllElements = () => {
        if (!curNode) return;
        const node = nodes.find(n => n.id === curNode);
        if (!node) return;
        const allReferenceIds: string[] = _.flatten(
            _.map(groupedContents, group =>
                group.map(content => content.contents.referenceId).filter((id): id is string => !!id)
            )
        );
        node.referenceIds = _.uniq([...(node.referenceIds ?? []), ...allReferenceIds]);
        s_startNode.setValue({ ...s_startNode.value as any });
    };

    const deSelectAllElements = () => {
        if (!curNode) return;
        const node = nodes.find(n => n.id === curNode);
        if (!node) return;
        const allReferenceIds: string[] = _.flatten(
            _.map(groupedContents, group =>
                group.map(content => content.contents.referenceId).filter((id): id is string => !!id)
            )
        );
        node.referenceIds = node.referenceIds?.filter(id => !allReferenceIds.includes(id)) ?? [];
        s_startNode.setValue({ ...s_startNode.value as any });
    };

    const node = nodes.find(n => n.id === curNode);
    const allReferenceIds: string[] = _.flatten(
        _.map(groupedContents, group =>
            group.map(content => content.contents.referenceId).filter((id): id is string => !!id)
        )
    );
    const isAnySelected = node?.referenceIds?.some(id => allReferenceIds.includes(id));



    return (
        <div>
            <T.SeqContainerOuter minWidth={minNodeWith} style={{ borderTop: "none" }}>
                {posts === undefined &&
                    <div style={{ gridColumnStart: 1, gridColumnEnd: 3, marginBottom: 10, padding: "0 10px" }}>
                        <LoaderInline active infoText={t("loading data")} />
                    </div>
                }
                <T.SeqContainer height={maxLeftHeight} color={selectedNodeColor ?? "@middleLightGrey"} ref={leftContainerRef} id="left">
                    <ImplementedElementsHeadlineOnlyAAO >
                        {t("implemented contents")}
                        {isLarge &&
                            <Button kind="fullTransparentButton" onClick={() => setShowExpandedView(!showExpandedView)} icon="editor text size" floated="right" tooltip={{ position: "left", offsetY: -15, tooltipText: showExpandedView ? t("compact view") : t("expanded view") }} />
                        }
                        <Button kind="halfTransparentButton" onClick={selectAllElements} icon="add" floated="right" tooltip={{ tooltipText: t("assign all elements to the selected phase"), background: "@accentGreen", fontColor: "#fff" }} />
                        {isAnySelected &&
                            <Button kind="halfTransparentButton" onClick={deSelectAllElements} icon="minus" floated="right" tooltip={{ tooltipText: t("remove all elements from the selected phase"), background: "@accentRed", fontColor: "#fff" }} />
                        }
                    </ImplementedElementsHeadlineOnlyAAO>
                    {_.map(groupedContents, (group, type) => {
                        const node = nodes.find(n => n.id === curNode);
                        const groupReferenceIds: string[] = group
                            .map(content => content.contents.referenceId)
                            .filter((id): id is string => !!id);
                        const isAnyElementSelected = node?.referenceIds?.some(id => groupReferenceIds.includes(id));
                        const allElementsSelected = groupReferenceIds.length > 0 && groupReferenceIds.every(id => node?.referenceIds?.includes(id));

                        const selectAllTypeElements = () => {
                            const { startNode, curNode } = getCurrentPlaylistNode(s_startNode, node);
                            if (!curNode) return;
                            curNode.referenceIds = _.uniq([...(curNode.referenceIds ?? []), ...groupReferenceIds]);
                            s_startNode.setValue(startNode);

                        };

                        const deSelectAllTypeElements = () => {
                            const { startNode, curNode } = getCurrentPlaylistNode(s_startNode, node);
                            if (!curNode) return;
                            curNode.referenceIds = curNode.referenceIds?.filter(id => !groupReferenceIds.includes(id)) ?? [];
                            s_startNode.setValue(startNode);
                        };

                        return (
                            <div key={type}>
                                <ImplementedElementsHeadlineOnlyAAO style={{ marginBottom: isLarge ? -5 : -5, fontSize: "1em" }}>
                                    <div style={{ position: "relative" }}>
                                        {t(type)}
                                        {isAnyElementSelected &&
                                            <div style={{ position: "absolute", right: showExpView ? 72 : 39, zIndex: 50, top: 1 }}>
                                                <Button
                                                    size="small"
                                                    icon="minus"
                                                    onClick={deSelectAllTypeElements} kind="transparentButton"
                                                    tooltip={{ tooltipText: t("remove all elements of the {{group}} group from the selected phase", { group: t(type) }), background: "@accentRed", fontColor: "#fff", position: "bottom" }}
                                                />
                                            </div>
                                        }
                                        <div style={{ position: "absolute", right: showExpView ? 40 : 7, zIndex: 50, top: 1 }}>
                                            <Button
                                                disabled={allElementsSelected}
                                                size="small"
                                                icon="add"
                                                onClick={selectAllTypeElements}
                                                kind={allElementsSelected ? "cancelButton" : "transparentButton"}
                                                cursor={allElementsSelected ? "default" : "pointer"}
                                                tooltip={{ tooltipText: allElementsSelected ? t("all elements are already in this phase") : t("assign all elements of the {{group}} group to the selected phase", { group: t(type) }), background: allElementsSelected ? "@darkGrey" : "@accentGreen", fontColor: "#fff", position: "top" }}
                                            />
                                        </div>
                                    </div>
                                </ImplementedElementsHeadlineOnlyAAO>
                                {_.map(_.sortBy(group, g => getPostHeadlineForContent(g as PlaylistContentDownloadDto).toUpperCase()), (p, i) => {
                                    if (p.contents.referenceId) {
                                        const pc = p.post;
                                        const node = _.find(nodes, (n) => n.id === curNode);
                                        const contentIdx = _.findIndex(node?.referenceIds, (r) => r === p.contents.referenceId);
                                        const isIn = contentIdx >= 0;
                                        const numberOfUsed: number = countElementUsageInNodes(nodes, p.contents.referenceId);
                                        const click = curNode
                                            ? () => {
                                                const { curNode, startNode } = getCurrentPlaylistNode(s_startNode, node);
                                                if (curNode) {
                                                    const idx = _.findIndex(curNode?.referenceIds, (r) => r === p.contents.referenceId);
                                                    if (idx >= 0) {
                                                        curNode.referenceIds = _.clone(curNode.referenceIds ?? []);
                                                        curNode.referenceIds.splice(contentIdx, 1);
                                                        s_startNode.setValue(startNode);

                                                    } else {
                                                        curNode.referenceIds = _.clone(curNode.referenceIds ?? []);
                                                        curNode.referenceIds.push(p.contents.referenceId!);
                                                        s_startNode.setValue(startNode);

                                                    }
                                                }
                                            }
                                            : undefined;

                                        if (pc)
                                            return (
                                                <div key={i}>
                                                    <PlaylistEditSequenceImplementedElement
                                                        content={p.contents}
                                                        notUsed={!numberOfUsed}
                                                        post={pc}
                                                        key={i}
                                                        isLinear
                                                        expandedView={showExpView}
                                                        select={click}
                                                        clickButtonProps={
                                                            click
                                                                ? {
                                                                    onClick: click,
                                                                    icon: isIn ? "checkbox checked" : "checkbox unchecked",
                                                                    tooltip: { tooltipText: isIn ? t("remove from selected phase") : t("assign to selected phase"), background: isIn ? "@accentRed" : "@accentGreen", fontColor: "white" },
                                                                    iconColor: isIn ? undefined : node?.color ?? "#000",
                                                                }
                                                                : undefined
                                                        }
                                                        numberOfUsed={numberOfUsed}
                                                    />
                                                </div>
                                            );

                                        return <div key={i} />;
                                    }
                                    return null;
                                })
                                }
                            </div>
                        )
                    })}
                </T.SeqContainer>
                <div ref={rightContainerRef} id="right">
                    <div style={{ paddingLeft: 40, paddingRight: 10 }}>
                        {nodes.length === 0 &&
                            <AGCTitle missingInput>
                                {t("current sequence is missing")}
                                <Icon name="exclamation circle" color="@accent" marginTop={5} style={{ marginRight: 5, float: "left" }} />
                            </AGCTitle>
                        }
                        <T.AddPhaseBarDiv>
                            <Button kind={nodes.length === 0 ? "primary" : "halfTransparentButton"} icon="add" content={t("add phase")} onClick={addNode} floated="left" />
                        </T.AddPhaseBarDiv>
                    </div>
                    <T.ImplContainer height={maxRightHeight} color={selectedNodeColor ?? "@middleLightGrey"} style={{ border: "none", padding: 0, marginTop: 0, marginBottom: 0 }}>
                        <SequenceContainerOuter color={selectedNodeColor ?? "@middleLightGrey"} >
                            <AutoGrid columns={10} gap={0}>
                                <AutoGridColumn span={10}>
                                    {(posts ?? []).length > 0 &&
                                        _.map(nodes, (node, i) => (
                                            <PlaylistEditLinearNode
                                                key={node.id}
                                                i={i}
                                                curNode={curNode}
                                                setCurNode={setCurNode}
                                                scrollHelper={scrollHelper}
                                                setContainerRef={setContainerRef}
                                                avlWidth={avlWidth}
                                            />
                                        )

                                        )}
                                    {nodes.length > 0 &&
                                        <>
                                            <T.NodeConnectorV>
                                                <Icon name="arrow down" style={{ position: "absolute", top: "50%", left: "50%", transform: "scaleY(1.2) translate(-50%, -50%)" }} color="@middleLightGrey" />
                                            </T.NodeConnectorV>
                                            <BtnCon>
                                                <Button kind="cancelButton" fluid content={t("add phase")} onClick={addNode} />
                                            </BtnCon>
                                        </>
                                    }
                                </AutoGridColumn>
                            </AutoGrid>
                        </SequenceContainerOuter>

                    </T.ImplContainer>
                </div>
            </T.SeqContainerOuter>
        </div>
    )
}
export default PlaylistEditSequenceLinear;